import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"
import type { SwitchProps } from "./Switch.types"

const switchVariants = cva(
  "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border transition-colors focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:dark:bg-primary-200 data-[state=checked]:border-primary-500 data-[state=unchecked]:bg-zinc-100 data-[state=unchecked]:border-zinc-200 min-w-max",
  {
    variants: {
      size: {
        sm: "h-4 w-7",
        md: "h-5 w-9",
        lg: "h-6 w-11",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

const thumbVariants = cva(
  "pointer-events-none block rounded-full border bg-white shadow-lg ring-0 transition-transform data-[state=checked]:border-primary-500 data-[state=checked]:dark:border-primary-300 data-[state=unchecked]:border-zinc-200 data-[state=unchecked]:translate-x-0",
  {
    variants: {
      size: {
        sm: "h-3 w-3 data-[state=checked]:translate-x-3",
        md: "h-4 w-4 data-[state=checked]:translate-x-4",
        lg: "h-5 w-5 data-[state=checked]:translate-x-5",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

const labelVariants = cva(
  "font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  {
    variants: {
      size: {
        sm: "text-sm",
        md: "text-base",
        lg: "text-lg",
      },
      isInvalid: {
        true: "text-destructive",
      }
    },
    defaultVariants: {
      size: "md",
    },
  }
)

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = "md", label, description, labelPosition = "end", isInvalid, id, ...props }, ref) => {
  const switchId = id || React.useId();

  const switchElement = (
    <SwitchPrimitives.Root
      id={switchId}
      className={cn(
        switchVariants({ size }),
        isInvalid
          ? "data-[state=unchecked]:border-destructive data-[state=unchecked]:bg-zinc-200 focus-visible:ring-red-400 focus-visible:ring-offset-red-100 dark:focus-visible:ring-red-500/50 dark:focus-visible:ring-offset-gray-900"
          : "focus-visible:ring-primary-400 focus-visible:ring-offset-primary-100 dark:focus-visible:ring-primary-500/50 dark:focus-visible:ring-offset-gray-900",
        className
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(thumbVariants({ size }))}
      />
    </SwitchPrimitives.Root>
  );

  if (!label && !description) {
    return switchElement;
  }

  const labelContainer = (
    <div className="grid gap-1.5 leading-none">
      {label && (
        <label
          htmlFor={switchId}
          className={cn(labelVariants({ size, isInvalid }))}
        >
          {label}
        </label>
      )}
      {description && (
        <p className={cn("text-sm text-muted-foreground", isInvalid && "text-destructive/80")}>
          {description}
        </p>
      )}
    </div>
  );

  const isReversed = labelPosition === "start";

  return (
    <div className={cn(
      "flex space-x-2",
      description ? "items-start" : "items-center",
      className
    )}>
      {isReversed ? (
        <>
          <div className="flex flex-1">{labelContainer}</div>
          <div className={cn(description && "pt-0.5", "flex shrink-0")}>{switchElement}</div>
        </>
      ) : (
        <>
          <div className={cn(description && "pt-0.5", "flex shrink-0")}>{switchElement}</div>
          {labelContainer}
        </>
      )}
    </div>
  )
})
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
